import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react'
import { landService } from '../../services'
import type { Land } from '@popety-io/types'

interface LandContextState {
  land: Land | null
  loading: boolean
  error: string | null
  fetchLand: (landId: string) => Promise<void>
  clearLand: () => void
  refetch: () => Promise<void>
}

const LandContext = createContext<LandContextState | undefined>(undefined)

export const useLandContext = () => {
  const context = useContext(LandContext)
  if (!context) {
    throw new Error('useLandContext must be used within a LandProvider')
  }
  return context
}

interface LandProviderProps {
  children: ReactNode
}

export const LandProvider: React.FC<LandProviderProps> = ({ children }) => {
  const [land, setLand] = useState<Land | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentLandId, setCurrentLandId] = useState<string | null>(null)

  const fetchLand = useCallback(async (landId: string) => {
    if (!landId) {
      setLand(null)
      setCurrentLandId(null)
      return
    }

    setLoading(true)
    setError(null)
    setCurrentLandId(landId)

    try {
      const landData = await landService.getLandForTooltip(landId)

      if (!landData.id && !landData.land_id) {
        throw new Error('Invalid land data: missing ID')
      }

      setLand(landData)
    } catch (error) {
      console.error('❌ Error fetching land data:', error)

      let errorMessage = 'Failed to load land data'

      if (error instanceof Error) {
        if (error.message.includes('404')) {
          errorMessage = `Land with ID "${landId}" not found`
        } else if (error.message.includes('403')) {
          errorMessage = 'Access denied to land data'
        } else if (error.message.includes('Network')) {
          errorMessage = 'Network error - please check your connection'
        } else {
          errorMessage = `Failed to load land data: ${error.message}`
        }
      }

      setError(errorMessage)
      setLand(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const clearLand = useCallback(() => {
    setLand(null)
    setError(null)
    setCurrentLandId(null)
  }, [])

  const refetch = useCallback(async () => {
    if (currentLandId) {
      await fetchLand(currentLandId)
    }
  }, [currentLandId, fetchLand])

  const contextValue: LandContextState = {
    land,
    loading,
    error,
    fetchLand,
    clearLand,
    refetch,
  }

  return (
    <LandContext.Provider value={contextValue}>{children}</LandContext.Provider>
  )
}
